/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable } from '@angular/core';
import { MessageSeverity } from '@gh/core-messages';
import { TranslationParams } from '@gh/core-mls';
import {
  DialogService,
  NotificationConfig,
  NotificationRef,
  NotificationService,
} from '@gh/core-ui';
import { NotificationContext } from './notification-context.service';
import { NotificationPanelComponent } from './notification-panel.component';

/**
 * This service allows to manage balloons shown in the overlay on top of the screen.
 */
@Injectable()
export class DefaultNotificationService extends NotificationService {

  constructor(private overlay: Overlay,
              private context: NotificationContext,
              private dialogService: DialogService) {
    super();

    this.initPanel();
  }

  /**
   * Shows neutral notification with the given message.
   * Click on notification closes this notification and opens success dialog.
   * Notification is automatically closed in 5 seconds.
   *
   * @param id
   * @param params
   * @returns {NotificationRef}
   */
  success(id: string, params?: TranslationParams): NotificationRef {
    const notification = this.show({
      severity: MessageSeverity.Success,
      messageId: id,
      messageParams: params,
      hideDelay: 5000,
    });

    notification.clicks.subscribe(() => {
      this.dialogService.success(id, params);
      notification.close();
    });

    return notification;
  }

  /**
   * Shows warning notification with the given message.
   * Click on notification closes this notification and opens warning dialog.
   *
   * @param id
   * @param params
   * @returns {NotificationRef}
   */
  warning(id: string, params?: TranslationParams): NotificationRef {
    const notification = this.show({
      severity: MessageSeverity.Warning,
      messageId: id,
      messageParams: params,
    });

    notification.clicks.subscribe(() => {
      this.dialogService.warning(id, params);
      notification.close();
    });

    return notification;
  }

  /**
   * Shows error notification with the given message.
   * Click on notification opens error dialog.
   *
   * @param id
   * @param params
   * @returns {NotificationRef}
   */
  error(id: string, params?: TranslationParams): NotificationRef {
    const notification = this.show({
      severity: MessageSeverity.Error,
      messageId: id,
      messageParams: params,
    });

    // this flag is necessary to prevent opening of dialog twice
    let opened = false;

    notification.clicks.subscribe(() => {
      if (!opened) {
        opened = true;
        this.dialogService.error(id, params).subscribe(() => opened = false);
      }
    });

    return notification;
  }

  /**
   * This method allows to show any notification message.
   * Unlike other methods this method does not open dialog on click.
   * Any behaviour should be attached manually using instance of {@link NotificationRef}.
   *
   * @param config
   * @returns {NotificationRef}
   */
  show(config: NotificationConfig): NotificationRef {
    return this.context.create(config);
  }

  /**
   * Closes all openeds notifications
   */
  closeAll(): void {
    this.context.closeAll();
  }

  private initPanel(): void {
    const overlay = this.createOverlay();

    overlay.attach(new ComponentPortal(NotificationPanelComponent));

    // liftOverlayOnTop(overlay);
  }

  private createOverlay(): OverlayRef {
    const overlayConfig = new OverlayConfig();
    overlayConfig.hasBackdrop = false;
    overlayConfig.positionStrategy = this.overlay.position().global().right('20px').top('20px');
    overlayConfig.panelClass = 'notification-overlay-pane';

    return this.overlay.create(overlayConfig);
  }
}
